import {Box, Group, Stack, Text, createStyles, getStylesRef} from '@mantine/core';
import React, {useState} from 'react';

import ErrorTriangle, {TriangleLocation, TriangleState} from 'notebook/tables/errors/ErrorTriangle';
import {UnsubmittedChangesError} from 'notebook/tables/excess/Util';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Modal from 'ui-library/modals/Modal';

type ResyncOption = 'keep' | 'remove';

const useResyncStyles = createStyles((theme) => ({
  optionCard: {
    border: `2px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    padding: theme.spacing.md,
    cursor: 'pointer',
    transition: 'border-color 150ms ease',
  },
  selected: {
    borderColor: theme.colors.blue[5],
  },
  recommendedBadge: {
    backgroundColor: theme.colors.blue[0],
    color: theme.colors.blue[7],
    borderRadius: theme.radius.xl,
    padding: '0px 4px',
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
  },
  previewLabel: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    color: theme.colors.gray[6],
    letterSpacing: '0.05em',
  },
  previewTable: {
    borderCollapse: 'collapse' as const,
    width: '100%',
    marginTop: 4,
  },
  previewTh: {
    padding: '4px 8px',
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    textAlign: 'left' as const,
    fontSize: theme.fontSizes.xs,
    color: theme.colors.gray[6],
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    maxWidth: 120,
    textOverflow: 'ellipsis',
  },
  'previewTd:first-of-type': {
    borderLeft: `1px solid ${theme.colors.gray[2]}`,
  },
  previewTd: {
    ref: getStylesRef('previewTd'),
    padding: '4px 6px',
    fontSize: theme.fontSizes.xs,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    borderRight: `1px solid ${theme.colors.gray[2]}`,
    maxWidth: 120,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  formulaCell: {
    color: theme.colors.green[7],
    fontFamily: theme.fontFamilyMonospace,
  },
}));

/**
 * Static three-column demo table (Entity / Formula column / Unsubmitted change) illustrating
 * the effect of the selected resync option. Cell values flip between keep and remove states.
 */
function ResyncDemoTable({isKeepMode}: {isKeepMode: boolean}): JSX.Element {
  const {classes, cx} = useResyncStyles();

  return (
    <Box mt="xs">
      <div className={classes.previewLabel}>After resync</div>
      <table className={classes.previewTable}>
        <thead>
          <tr>
            <th className={classes.previewTh}>Entity</th>
            <th className={classes.previewTh}>Formula column</th>
            <th className={classes.previewTh}>Unsubmitted change</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className={classes.previewTd}>Sample-001</td>
            <td className={cx(classes.previewTd, isKeepMode && classes.formulaCell)}>
              {isKeepMode ? '=LOOKUP("entity_id")' : 'ABC-001'}
            </td>
            <td className={classes.previewTd}>
              <Box pos="relative">
                {isKeepMode ? '(Unsubmitted value)' : '(Upstream value)'}
                {isKeepMode && (
                  <ErrorTriangle
                    location={TriangleLocation.CELL}
                    state={TriangleState.UNSUBMITTED_CHANGES}
                    error={
                      new UnsubmittedChangesError(
                        'This value was updated after the table was last submitted. Previously submitted value: (Upstream value)'
                      )
                    }
                  />
                )}
              </Box>
            </td>
          </tr>
        </tbody>
      </table>
    </Box>
  );
}

function getIntroText(formulaPresent: boolean, hasUnsubmittedChanges: boolean): string {
  if (formulaPresent && hasUnsubmittedChanges) {
    return 'This table has formulas and unsubmitted changes in submitted rows. Choose how to handle them.';
  }
  if (formulaPresent) {
    return 'This table has formulas in submitted rows. Choose how to handle them.';
  }
  return 'This table has unsubmitted changes in submitted rows. Choose how to handle them.';
}

/**
 * Modal shown when the user clicks "Re-sync table" and any of the following conditions are true:
 *   - formulaPresent: submitted rows contain formulas
 *   - unsubmittedChangesInSubmittedRow: submitted rows have unsubmitted edits that will be lost
 *   - possibleEditsInUnsubmittedRows: unsubmitted rows may have user edits that will be overwritten
 *
 * Presents two options:
 *   - Keep (default): calls onRequestKeepFormulas → entities-only refresh preserves formula cells and edits
 *   - Remove: calls onRequestRemoveFormulas → full resync overwrites everything with live values
 */
export function ResyncOptionsModal({
  onRequestClose,
  onRequestKeepFormulas,
  onRequestRemoveFormulas,
  unsubmittedChangesInSubmittedRow,
  possibleEditsInUnsubmittedRows,
  formulaPresent,
}: {
  onRequestClose: () => void;
  onRequestKeepFormulas: () => void;
  onRequestRemoveFormulas: () => void;
  unsubmittedChangesInSubmittedRow: boolean;
  possibleEditsInUnsubmittedRows: boolean;
  formulaPresent: boolean;
}): JSX.Element {
  const [selectedOption, setSelectedOption] = useState<ResyncOption>('keep');
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useResyncStyles();

  const isKeepMode = selectedOption === 'keep';
  const hasUnsubmittedChanges = unsubmittedChangesInSubmittedRow || possibleEditsInUnsubmittedRows;

  const handleSubmit = (): void => {
    if (isKeepMode) {
      onRequestKeepFormulas();
    } else {
      onRequestRemoveFormulas();
    }
    // Close the modal after dispatching the action. legacyRunModal wraps onRequestClose to
    // unmount the modal container, so calling it here is what actually closes the modal.
    onRequestClose();
  };

  const buttons = [
    {text: 'Cancel', cancel: true},
    {
      text: isKeepMode ? 'Resync' : 'Resync and remove formulas and unsubmitted changes',
      submit: true,
      dataTestAttrs: {component: 'ResyncOptionsModal', element: 'submitButton'},
    },
  ];

  return (
    <Modal
      title="Resync table"
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      buttons={buttons}
      dataTestAttrs={{component: 'ResyncOptionsModal', element: 'root'}}
    >
      <Stack spacing="sm">
        <Text size="sm">{getIntroText(formulaPresent, hasUnsubmittedChanges)}</Text>

        <Box
          className={cx(classes.optionCard, isKeepMode && classes.selected)}
          onClick={() => {
            setSelectedOption('keep');
          }}
          {...getTestProps({component: 'ResyncOptionsModal', element: 'keepOption'})}
        >
          <Stack spacing="xs">
            <Group spacing="xs" noWrap={true}>
              <input
                type="radio"
                style={{margin: 0}}
                checked={isKeepMode}
                onChange={() => {
                  setSelectedOption('keep');
                }}
              />
              <Text size="sm" weight={600} sx={{flexGrow: 1}}>
                Update values and keep formulas and unsubmitted changes
              </Text>
              <div className={classes.recommendedBadge}>RECOMMENDED</div>
            </Group>
            {isKeepMode && (
              <>
                <Text size="xs" color="dimmed">
                  Values will be recalculated using the latest source data. Formulas and your unsubmitted
                  changes will both be preserved.
                </Text>
                <ResyncDemoTable isKeepMode={true} />
              </>
            )}
          </Stack>
        </Box>

        <Box
          className={cx(classes.optionCard, !isKeepMode && classes.selected)}
          onClick={() => {
            setSelectedOption('remove');
          }}
          {...getTestProps({component: 'ResyncOptionsModal', element: 'removeOption'})}
        >
          <Stack spacing="xs">
            <Group spacing="xs" noWrap={true}>
              <input
                type="radio"
                checked={!isKeepMode}
                onChange={() => {
                  setSelectedOption('remove');
                }}
              />
              <Text size="sm" weight={600}>
                Update values and remove formulas and unsubmitted changes
              </Text>
            </Group>
            {!isKeepMode && (
              <>
                <Text size="xs" color="dimmed">
                  Values will be recalculated and written as plain values. Formulas will be permanently
                  removed and unsubmitted changes will be overwritten.
                </Text>
                <ResyncDemoTable isKeepMode={false} />
                <InlineBanner
                  bannerType={InlineBannerType.WARNING}
                  text="Consider keeping formulas unless you specifically need static values."
                />
              </>
            )}
          </Stack>
        </Box>
      </Stack>
    </Modal>
  );
}
